import { useState, useEffect, useCallback } from 'react';
import { Send, History, Settings, Plus, Mail, Inbox, Bell, Users } from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { useAdminNotifications } from '@/hooks/useNotifications';
import { NotificationPreferences } from '@/components/notifications/NotificationPreferences';
import { NotificationHistory } from '@/components/notifications/admin/NotificationHistory';
import { PushSubscribers } from '@/components/notifications/admin/PushSubscribers';
import { SystemNotificationConfig } from '@/components/notifications/admin/SystemNotificationConfig';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { SendNotificationModal } from '@/components/notifications/admin/SendNotificationModal';
import { notificationService } from '@/services/notification.service';
import { notificationSocketService } from '@/services/notification-socket.service';
import { useNotificationStore } from '@/stores/notification.store';
import { UserNotification } from '@/types/notification.types';
import Pagination from '@/components/ui/Pagination';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

type TabType = 'history' | 'preferences' | 'my-notifications' | 'subscribers';

// URL-from-tab and tab-from-URL helpers — keeps the two in sync.
const urlForTab = (t: TabType) =>
  t === 'preferences'
    ? '/admin/notifications/settings'
    : t === 'my-notifications'
    ? '/admin/notifications/my-notifications'
    : t === 'subscribers'
    ? '/admin/notifications/subscribers'
    : '/admin/notifications';

const tabFromPath = (path: string): TabType => {
  if (path.includes('/my-notifications')) return 'my-notifications';
  if (path.includes('/settings')) return 'preferences';
  if (path.includes('/subscribers')) return 'subscribers';
  return 'history';
};

export default function AdminNotificationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Derived from URL on every render — no useState/useEffect sync gap.
  const activeTab: TabType = tabFromPath(location.pathname);
  const [showSendModal, setShowSendModal] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const { getNotificationMetrics } = useAdminNotifications();

  // Fetch metrics
  const { data: metricsData, refetch: refetchMetrics } = useQuery({
    queryKey: ['notification-metrics'],
    queryFn: getNotificationMetrics,
  });

  const metrics = metricsData?.data;

  const tabs: { id: TabType; label: string; icon: typeof History }[] = [
    { id: 'history', label: 'Notification History', icon: History },
    { id: 'subscribers', label: 'Push Subscribers', icon: Users },
    { id: 'preferences', label: 'Settings', icon: Settings },
    { id: 'my-notifications', label: 'My Notifications', icon: Inbox },
  ];

  const handleSendSuccess = () => {
    refetchMetrics();
    setHistoryRefreshKey(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      {/* Gradient Header with Metrics */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Notification Management</h1>
            <p className="text-blue-100">
              Send, schedule, and track notifications across your platform
            </p>
          </div>

          <button
            onClick={() => setShowSendModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Send Notification</span>
          </button>
        </div>

        {/* Metrics Cards inside gradient */}
        {metrics ? (
          <>
            {/* Row 1: Total Created + By Channel */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Total Created</p>
                  <p className="text-2xl font-bold">
                    {metrics.total_created?.toLocaleString() ?? 0}
                  </p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">In-App</p>
                  <p className="text-2xl font-bold">
                    {(metrics.by_channel?.IN_APP ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Email</p>
                  <p className="text-2xl font-bold">
                    {(metrics.by_channel?.EMAIL ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Push</p>
                  <p className="text-2xl font-bold">
                    {(metrics.by_channel?.PUSH ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Row 2: Sent / Scheduled / Cancelled / Failed — hidden, data still available via API */}
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4 animate-pulse">
                <div className="p-3 bg-white/20 rounded-lg">
                  <SkeletonLoader type="custom" className="w-6 h-6 rounded" />
                </div>
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="text" className="h-3 w-20 bg-white/20" />
                  <SkeletonLoader type="text" className="h-7 w-16 bg-white/20" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(urlForTab(tab.id))}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'history' && (
          <NotificationHistory onActionComplete={handleSendSuccess} refreshKey={historyRefreshKey} />
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <NotificationPreferences />
            <SystemNotificationConfig />
          </div>
        )}

        {activeTab === 'my-notifications' && (
          <MyNotificationsTab />
        )}

        {activeTab === 'subscribers' && (
          <PushSubscribers />
        )}
      </div>

      {/* Send Notification Modal */}
      <SendNotificationModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSuccess={handleSendSuccess}
      />
    </div>
  );
}

// ======================================================================
// My Notifications tab — admin's own received notifications (same data
// as learner, using POST /notifications). Written inline to match the
// Settings tab card style without reusing NotificationCenter.
// ======================================================================

type FilterTab = 'ALL' | 'IN_APP' | 'EMAIL' | 'PUSH';

function MyNotificationsTab() {
  const syncFromServer = useNotificationStore((s) => s.syncFromServer);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filterTab, setFilterTab] = useState<FilterTab>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchPage = useCallback(async () => {
    setIsLoading(true);
    const channel = filterTab !== 'ALL' ? filterTab : undefined;
    const response = await notificationService.getNotifications({
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
      channel,
    });
    if (response.success) {
      setNotifications(response.data);
      setTotalCount(response.meta_data?.total ?? response.data.length);
    }
    setIsLoading(false);
  }, [currentPage, pageSize, filterTab]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleMarkAsRead = useCallback(async (notification_id: string) => {
    notificationSocketService.emitMarkAsRead(notification_id);
    await notificationService.markAsRead(notification_id);
    await fetchPage();
    await syncFromServer();
  }, [fetchPage, syncFromServer]);


  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header — matches Settings tab style */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-xl">
              <Inbox className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">My Notifications</h2>
              <p className="text-sm text-gray-500">Your notifications</p>
            </div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 mt-4">
          {(['ALL', 'IN_APP', 'EMAIL', 'PUSH'] as FilterTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => { setFilterTab(tab); setCurrentPage(1); }}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                filterTab === tab
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab === 'ALL' ? 'All' : tab === 'IN_APP' ? 'In-App' : tab === 'EMAIL' ? 'Email' : 'Push'}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications list */}
      <div className="divide-y divide-gray-100">
        {isLoading && notifications.length === 0 ? (
          <div className="p-4 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-4">
                <SkeletonLoader type="avatar" className="flex-shrink-0 w-12 h-12" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="text" className="h-3 w-20" />
                  <SkeletonLoader type="text" className="h-4 w-3/4" />
                  <SkeletonLoader type="text" className="h-3 w-1/2" />
                  <SkeletonLoader type="text" className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {filterTab !== 'ALL' ? `No ${filterTab === 'IN_APP' ? 'in-app' : filterTab === 'EMAIL' ? 'email' : 'push'} notifications` : 'No notifications yet'}
            </h3>
            <p className="text-gray-500 text-center max-w-sm">
              {filterTab !== 'ALL'
                ? 'Switch to "All" to see all notifications.'
                : "We'll notify you when something new arrives."}
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification._id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              showActions
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          itemsPerPage={pageSize}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
}
